import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { ListboxModule } from 'primeng/listbox';
import { StepperModule } from 'primeng/stepper';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { DeviceContextService } from '../device-context.service';
import { CloudApiService, FirmwareEntry, Package, PackageDetails, PackageVersion } from '../../../services/cloud-api.service';
import { DeviceAction, DeviceActionStatus, DeviceActionType } from 'src/api/device-action';
import { ActionItemComponent } from './action-item.component';
import { MigrateCloudDialog } from './migrate-cloud-dialog';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

interface ActionOption {
    label: string;
    value: DeviceActionType;
}

interface VersionOption {
    label: string;
    value: string; // composite: packageId:versionId
    packageId: string;
    packageName: string;
    versionId: string;
    assemblyVersion: string;
    isLatest: boolean;
    isDebugBuild: boolean;
}

interface FirmwareVersionOption {
    label: string;
    value: string; // firmwareId
    firmwareId: string;
    version: string;
    fileName: string;
    isCurrent: boolean;
}

// Constants
const RECENT_ACTION_THRESHOLD_HOURS = 24;
const ACTIVE_STATUSES: DeviceActionStatus[] = ['Pending', 'Dispatched'];
const COMPLETED_STATUSES: DeviceActionStatus[] = ['Succeeded', 'Failed', 'Cancelled'];

@Component({
    selector: 'app-device-actions',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DatePicker, ListboxModule, StepperModule, SelectModule, TagModule, DialogModule, ActionItemComponent, MigrateCloudDialog, EmptyStateComponent],
    templateUrl: './device-actions.html',
    styleUrl: './device-actions.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceActions {
    private ctx = inject(DeviceContextService);
    private api = inject(CloudApiService);

    private readonly allActionOptions: ActionOption[] = [
        { label: 'Reboot Device', value: 'Reboot' },
        { label: 'Restart Services', value: 'Restart' },
        { label: 'Perform Update', value: 'Update' },
        { label: 'Firmware Update', value: 'FirmwareUpdate' }
    ];

    readonly actionOptions = computed(() => {
        const device = this.ctx.device();
        const isAppliance = device?.payload?.devicePlatform === 'Appliance';

        // Filter out Reboot and FirmwareUpdate options for non-appliance devices
        if (isAppliance) {
            return this.allActionOptions;
        } else {
            return this.allActionOptions.filter((opt) => opt.value !== 'Reboot' && opt.value !== 'FirmwareUpdate');
        }
    });

    readonly actions = signal<DeviceAction[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);
    readonly submitting = signal(false);
    readonly creationError = signal<string | null>(null);

    readonly showCreateDialog = signal(false);
    readonly showAllActionsDialog = signal(false);
    readonly showMigrateDialog = signal(false);

    // Migrating a device to another cloud environment is superadmin-only
    readonly isSuperAdmin = computed(() => this.api.currentUser()?.superAdmin === true);

    readonly actionType = signal<DeviceActionType>('Restart');
    readonly scheduledFor = signal<Date | null>(null);
    readonly scheduleError = signal<string | null>(null);
    readonly payloadText = signal<string>('');
    readonly stepIndex = signal(1);

    // Version selection for Update action (all versions from compatible packages)
    readonly versions = signal<VersionOption[]>([]);
    readonly versionsLoading = signal(false);
    readonly versionsError = signal<string | null>(null);
    readonly selectedVersionKey = signal<string | null>(null); // packageId:versionId

    // Firmware version selection for FirmwareUpdate action
    readonly firmwareVersions = signal<FirmwareVersionOption[]>([]);
    readonly firmwareVersionsLoading = signal(false);
    readonly firmwareVersionsError = signal<string | null>(null);
    readonly selectedFirmwareKey = signal<string | null>(null); // firmwareId

    // Computed: does the current action type require options?
    readonly actionRequiresOptions = computed(() => {
        return this.actionType() === 'Update' || this.actionType() === 'FirmwareUpdate';
    });

    // Computed: get the selected version option
    readonly selectedVersion = computed(() => {
        const key = this.selectedVersionKey();
        if (!key) return null;
        return this.versions().find((v) => v.value === key) ?? null;
    });

    // Computed: get the selected firmware version option
    readonly selectedFirmwareVersion = computed(() => {
        const key = this.selectedFirmwareKey();
        if (!key) return null;
        return this.firmwareVersions().find((v) => v.value === key) ?? null;
    });

    // Computed: get the current device assembly version from payload
    readonly currentDeviceVersion = computed(() => {
        const device = this.ctx.device();
        const version = device?.payload?.appAssemblyVersion;
        return typeof version === 'string' ? version : null;
    });

    // Computed: get the current device firmware version
    readonly currentFirmwareVersion = computed(() => {
        const device = this.ctx.device();
        const version = device?.payload?.firmwareVersion;
        return typeof version === 'string' ? version : null;
    });

    // Computed: filter actions to show pending/scheduled/recently completed only
    readonly filteredActions = computed(() => {
        const allActions = this.actions();
        const now = Date.now();
        const recentThreshold = RECENT_ACTION_THRESHOLD_HOURS * 60 * 60 * 1000; // Convert hours to milliseconds

        return allActions.filter((action) => {
            // Show all active actions (pending or dispatched)
            if (ACTIVE_STATUSES.includes(action.status)) {
                return true;
            }

            // Show recently completed actions (Succeeded, Failed, or Cancelled within threshold)
            if (COMPLETED_STATUSES.includes(action.status)) {
                if (action.completedUtc) {
                    const completedTime = new Date(action.completedUtc).getTime();
                    const timeSinceCompleted = now - completedTime;
                    return timeSinceCompleted <= recentThreshold;
                }
                // If no completedUtc but has completed status, still show it (edge case)
                return true;
            }

            return false;
        });
    });

    // Computed: check if there are more actions than shown in filtered view
    readonly hasMoreActions = computed(() => {
        return this.actions().length > this.filteredActions().length;
    });

    private fetchToken = 0;
    private versionFetchToken = 0;
    private firmwareFetchToken = 0;

    constructor() {
        effect((cleanup) => {
            const device = this.ctx.device();
            const deviceId = device?.id;
            if (!deviceId) {
                this.actions.set([]);
                this.error.set(null);
                return;
            }

            const requestId = ++this.fetchToken;
            this.loading.set(true);
            this.error.set(null);

            this.api
                .getDeviceActions(deviceId, { limit: 50 })
                .then((list) => {
                    if (requestId === this.fetchToken) {
                        this.actions.set(this.sortActions(list));
                    }
                })
                .catch((err) => {
                    if (requestId === this.fetchToken) {
                        this.error.set(this.toErrorMessage(err));
                    }
                })
                .finally(() => {
                    if (requestId === this.fetchToken) {
                        this.loading.set(false);
                    }
                });

            const sub = this.api.subscribeToDeviceActions(deviceId).subscribe((list) => {
                this.actions.set(this.sortActions(list));
            });

            cleanup(() => {
                sub.unsubscribe();
            });
        });

        // Ensure selected action type is valid when options change
        effect(() => {
            const availableOptions = this.actionOptions();
            const currentAction = this.actionType();
            const isValid = availableOptions.some((opt) => opt.value === currentAction);

            if (!isValid && availableOptions.length > 0) {
                this.actionType.set(availableOptions[0].value);
            }
        });

        // Fetch package versions when device namespace changes (filtered by device's packageNamespace)
        effect(() => {
            const device = this.ctx.device();
            const namespaceId = device?.appNamespaceId;
            const devicePackageNamespace = device?.payload?.appNamespace;

            if (!namespaceId) {
                this.versions.set([]);
                this.versionsError.set(null);
                return;
            }

            const requestId = ++this.versionFetchToken;
            this.versionsLoading.set(true);
            this.versionsError.set(null);

            this.fetchAllVersions(namespaceId, devicePackageNamespace, requestId);
        });

        // Fetch firmware versions when device model is available
        effect(() => {
            const device = this.ctx.device();
            const model = device?.model;

            if (!model) {
                this.firmwareVersions.set([]);
                this.firmwareVersionsError.set(null);
                return;
            }

            const requestId = ++this.firmwareFetchToken;
            this.firmwareVersionsLoading.set(true);
            this.firmwareVersionsError.set(null);

            this.fetchFirmwareVersions(model, device?.payload?.firmwareVersion, requestId);
        });
    }

    private async fetchAllVersions(namespaceId: string, devicePackageNamespace: string | undefined, requestId: number): Promise<void> {
        try {
            const packageList = await this.api.getPackages(namespaceId);
            if (requestId !== this.versionFetchToken) return;

            // Filter packages to only show those with matching programNamespace (if device has reported one)
            const filteredPackages = devicePackageNamespace ? packageList.filter((pkg) => pkg.programNamespace === devicePackageNamespace) : packageList;

            // Fetch details for each package to get all versions
            const versionOptions: VersionOption[] = [];
            for (const pkg of filteredPackages) {
                try {
                    // The whole history, so any version can be a rollback target.
                    const versions = await this.api.getAllPackageVersions(pkg.id);
                    if (requestId !== this.versionFetchToken) return;

                    // Add each version as a selectable option
                    for (const version of versions) {
                        versionOptions.push({
                            label: `${pkg.name} v${version.assemblyVersion}`,
                            value: `${pkg.id}:${version.id}`,
                            packageId: pkg.id,
                            packageName: pkg.name,
                            versionId: version.id,
                            assemblyVersion: version.assemblyVersion,
                            isLatest: version.isLatest,
                            isDebugBuild: version.isDebugBuild ?? false
                        });
                    }
                } catch {
                    // Skip packages we can't fetch details for
                }
            }

            if (requestId === this.versionFetchToken) {
                this.versions.set(versionOptions);
            }
        } catch (err) {
            if (requestId === this.versionFetchToken) {
                this.versionsError.set(this.toErrorMessage(err));
            }
        } finally {
            if (requestId === this.versionFetchToken) {
                this.versionsLoading.set(false);
            }
        }
    }

    private async fetchFirmwareVersions(model: string, currentVersion: string | undefined, requestId: number): Promise<void> {
        try {
            const firmwareList = await this.api.getFirmwareList(model);
            if (requestId !== this.firmwareFetchToken) return;

            const options: FirmwareVersionOption[] = firmwareList
                .filter((fw) => {
                    // Allow same version (for re-apply/testing) but no downgrades
                    if (!currentVersion) return true;
                    return this.compareFirmwareVersions(fw.version, currentVersion) >= 0;
                })
                .map((fw) => ({
                    label: `${fw.name} v${fw.version}`,
                    value: fw.id,
                    firmwareId: fw.id,
                    version: fw.version,
                    fileName: fw.originalFileName,
                    isCurrent: !!currentVersion && this.compareFirmwareVersions(fw.version, currentVersion) === 0
                }));

            if (requestId === this.firmwareFetchToken) {
                this.firmwareVersions.set(options);
            }
        } catch (err) {
            if (requestId === this.firmwareFetchToken) {
                this.firmwareVersionsError.set(this.toErrorMessage(err));
            }
        } finally {
            if (requestId === this.firmwareFetchToken) {
                this.firmwareVersionsLoading.set(false);
            }
        }
    }

    private compareFirmwareVersions(a: string, b: string): number {
        // Only compare first 3 segments (major.minor.patch); ignore 4th build segment
        const partsA = a.split('.').slice(0, 3).map(Number);
        const partsB = b.split('.').slice(0, 3).map(Number);
        for (let i = 0; i < 3; i++) {
            const numA = partsA[i] || 0;
            const numB = partsB[i] || 0;
            if (numA !== numB) return numA - numB;
        }
        return 0;
    }

    refresh(): void {
        const deviceId = this.ctx.device()?.id;
        if (!deviceId) return;
        this.loading.set(true);
        this.error.set(null);
        const requestId = ++this.fetchToken;
        this.api
            .getDeviceActions(deviceId, { limit: 50 })
            .then((list) => {
                if (requestId === this.fetchToken) {
                    this.actions.set(this.sortActions(list));
                }
            })
            .catch((err) => {
                if (requestId === this.fetchToken) {
                    this.error.set(this.toErrorMessage(err));
                }
            })
            .finally(() => {
                if (requestId === this.fetchToken) {
                    this.loading.set(false);
                }
            });
    }

    async queueAction(): Promise<void> {
        const deviceId = this.ctx.device()?.id;
        if (!deviceId) {
            this.creationError.set('Device context missing.');
            return;
        }

        let payload: any = undefined;
        const actionType = this.actionType();

        // For Update actions, use the selected version
        if (actionType === 'Update') {
            const selectedVer = this.selectedVersion();
            if (!selectedVer) {
                this.creationError.set('Please select a package version to deploy.');
                return;
            }
            payload = {
                packageId: selectedVer.packageId,
                versionId: selectedVer.versionId,
                version: selectedVer.assemblyVersion
            };
        } else if (actionType === 'FirmwareUpdate') {
            const selectedFw = this.selectedFirmwareVersion();
            if (!selectedFw) {
                this.creationError.set('Please select a firmware version.');
                return;
            }
            // Fetch the download URL for the selected firmware
            let downloadInfo: { downloadUrl: string; fileName: string };
            try {
                downloadInfo = await this.api.getFirmwareDownloadUrl(selectedFw.firmwareId);
            } catch (err) {
                this.creationError.set('Failed to get firmware download URL.');
                return;
            }
            payload = {
                firmwareId: selectedFw.firmwareId,
                version: selectedFw.version,
                fileName: downloadInfo.fileName,
                downloadUrl: downloadInfo.downloadUrl
            };
        } else {
            // For other action types, use the raw payload if provided
            const payloadRaw = this.payloadText().trim();
            if (payloadRaw) {
                try {
                    payload = JSON.parse(payloadRaw);
                } catch (err) {
                    this.creationError.set('Payload must be valid JSON.');
                    return;
                }
            }
        }

        const schedule = this.scheduledFor();
        if (schedule && Number.isNaN(schedule.getTime())) {
            this.creationError.set('Scheduled date is invalid.');
            return;
        }
        this.submitting.set(true);
        this.creationError.set(null);
        try {
            await this.api.createDeviceAction(deviceId, {
                actionType: actionType,
                payload,
                scheduledForUtc: schedule ? schedule.toISOString() : undefined
            });
            this.resetForm();
            this.showCreateDialog.set(false);
        } catch (err) {
            this.creationError.set(this.toErrorMessage(err));
        } finally {
            this.submitting.set(false);
        }
    }

    openActionDialog(): void {
        this.showCreateDialog.set(true);
    }

    openAllActionsDialog(): void {
        this.showAllActionsDialog.set(true);
    }

    formatDate(value?: string | Date | null): string {
        if (!value) return '—';
        try {
            const dt = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(dt.getTime())) return typeof value === 'string' ? value : '—';
            return dt.toLocaleString();
        } catch {
            return typeof value === 'string' ? value : '—';
        }
    }

    formatRelative(value?: string | Date | null): string {
        if (!value) return '';
        const dt = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(dt.getTime())) return '';
        const diff = dt.getTime() - Date.now();
        const future = diff >= 0;
        const absSeconds = Math.floor(Math.abs(diff) / 1000);
        const format = (value: number, singular: string, plural: string) => `${future ? 'in ' : ''}${value} ${value === 1 ? singular : plural}${future ? '' : ' ago'}`;
        if (absSeconds < 60) return format(Math.max(absSeconds, 1), 'second', 'seconds');
        const minutes = Math.floor(absSeconds / 60);
        if (minutes < 60) return format(minutes, 'minute', 'minutes');
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return format(hours, 'hour', 'hours');
        const days = Math.floor(hours / 24);
        if (days < 7) return format(days, 'day', 'days');
        const weeks = Math.floor(days / 7);
        if (weeks < 5) return format(weeks, 'week', 'weeks');
        const months = Math.floor(days / 30);
        if (months < 12) return format(months, 'month', 'months');
        const years = Math.floor(days / 365);
        return format(years, 'year', 'years');
    }

    clearSchedule(): void {
        this.scheduledFor.set(null);
        this.scheduleError.set(null);
    }

    scheduleSummary(): string {
        const value = this.scheduledFor();
        if (!value) return 'Dispatch on next check-in';
        if (Number.isNaN(value.getTime())) return 'Invalid schedule';
        const relative = this.formatRelative(value);
        return `${value.toLocaleString()}${relative ? ` (${relative})` : ''}`;
    }

    onScheduleChange(value: Date | null): void {
        if (value && Number.isNaN(value.getTime())) {
            this.scheduleError.set('Select a valid date and time.');
            return;
        }
        if (value) {
            // Round minutes to nearest 5-minute interval (0, 5, 10, 15, etc.)
            const rounded = new Date(value);
            const minutes = rounded.getMinutes();
            const roundedMinutes = Math.round(minutes / 5) * 5;
            rounded.setMinutes(roundedMinutes, 0, 0); // Also reset seconds and milliseconds
            this.scheduledFor.set(rounded);
        } else {
            this.scheduledFor.set(null);
        }
        this.scheduleError.set(null);
    }

    nextStep(): void {
        const current = this.stepIndex();
        if (current === 1) {
            const scheduled = this.scheduledFor();
            if (scheduled && Number.isNaN(scheduled.getTime())) {
                this.scheduleError.set('Select a valid date and time.');
                return;
            }
        }
        this.scheduleError.set(null);
        this.stepIndex.set(Math.min(current + 1, 3));
    }

    previousStep(): void {
        const current = this.stepIndex();
        this.stepIndex.set(Math.max(current - 1, 1));
    }

    resetForm(): void {
        // Always default to Restart
        this.actionType.set('Restart');
        this.scheduledFor.set(null);
        this.payloadText.set('');
        this.scheduleError.set(null);
        this.stepIndex.set(1);
        this.selectedVersionKey.set(null);
        this.selectedFirmwareKey.set(null);
    }

    private sortActions(list: DeviceAction[]): DeviceAction[] {
        return [...list].sort((a, b) => {
            // Helper to check if action is outstanding (in progress)
            const isOutstanding = (action: DeviceAction): boolean => {
                return action.status === 'Pending' || action.status === 'Dispatched';
            };

            // Get the most recent timestamp for each action (last updated)
            const getLastUpdated = (action: DeviceAction): number => {
                const times = [action.completedUtc, action.dispatchedUtc, action.createdUtc].filter(Boolean).map((t) => new Date(t!).getTime());
                return times.length > 0 ? Math.max(...times) : 0;
            };

            const aOutstanding = isOutstanding(a);
            const bOutstanding = isOutstanding(b);

            // First priority: outstanding actions come before completed actions
            if (aOutstanding && !bOutstanding) return -1;
            if (!aOutstanding && bOutstanding) return 1;

            // Second priority: sort by last updated time (most recent first)
            const aTime = getLastUpdated(a);
            const bTime = getLastUpdated(b);
            return bTime - aTime;
        });
    }

    private toErrorMessage(err: any): string {
        if (!err) return 'Unknown error';
        if (typeof err === 'string') return err;
        if (err.error) {
            if (typeof err.error === 'string') return err.error;
            if (err.error?.message) return err.error.message;
        }
        if (err.message) return err.message;
        return 'Request failed';
    }
}
